import { useState } from "react";
import { apiCall } from "./api";
import RulesPanel, { RulesButton } from "./rules-panel";
import FeedbackPanel, { FeedbackButton } from "./feedback-panel";
import blackOwlLogo from "./assets/black-owl-logo.png";
import { FONT_BODY, FONT_DISPLAY } from "./theme";

// --- Owl logo image (header) ---
function OwlIcon() {
  return (
    <img
      src={blackOwlLogo}
      alt="Black Owl"
      style={{ display: "block", width: 76, height: "auto", objectFit: "contain" }}
    />
  );
}

// --- Background (reused from login) ---
function FieldBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: "linear-gradient(165deg, #0a0a14 0%, #0f1a0f 40%, #0a0a14 100%)",
    }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute", left: 0, right: 0,
          top: `${(i + 1) * 8}%`, height: "1px",
          background: `rgba(201, 162, 39, ${i === 5 ? 0.12 : 0.04})`,
        }}/>
      ))}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
      }}/>
    </div>
  );
}

// --- Reusable input ---
function Input({ label, value, onChange, placeholder, icon, maxLength }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: "16px" }}>
      <label style={{
        display: "block", fontSize: "11px", fontWeight: 600,
        letterSpacing: "2px", textTransform: "uppercase",
        color: focused ? "#c9a227" : "#7a7a8e",
        marginBottom: "8px", transition: "color 0.3s ease",
        fontFamily: FONT_BODY,
      }}>{label}</label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <span style={{
          position: "absolute", left: "14px", fontSize: "16px",
          color: focused ? "#c9a227" : "#4a4a5e",
          transition: "color 0.3s ease", pointerEvents: "none",
        }}>{icon}</span>
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", padding: "14px 14px 14px 42px",
            fontSize: "15px", fontFamily: FONT_BODY,
            color: "#e8e6df",
            background: focused ? "rgba(201, 162, 39, 0.06)" : "rgba(255,255,255,0.03)",
            border: `1.5px solid ${focused ? "#c9a227" : "rgba(255,255,255,0.08)"}`,
            borderRadius: "8px", outline: "none",
            transition: "all 0.3s ease", boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}

// --- Action card component ---
function ActionCard({ title, description, children, expanded, onToggle }) {
  return (
    <div style={{
      background: expanded ? "rgba(201, 162, 39, 0.05)" : "rgba(255,255,255,0.02)",
      border: `1.5px solid ${expanded ? "rgba(201, 162, 39, 0.25)" : "rgba(255,255,255,0.06)"}`,
      borderRadius: "12px",
      overflow: "hidden",
      transition: "all 0.3s ease",
      marginBottom: "16px",
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "none", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div>
          <h3 style={{
            fontSize: "16px", fontWeight: 700, color: "#c9a227",
            margin: "0 0 4px", letterSpacing: "1px",
            fontFamily: FONT_BODY,
          }}>{title}</h3>
          <p style={{
            fontSize: "13px", color: "#5a5a6e", margin: 0,
            fontFamily: FONT_BODY,
          }}>{description}</p>
        </div>
        <span style={{
          fontSize: "20px", color: "#c9a227",
          transform: expanded ? "rotate(45deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease",
        }}>+</span>
      </button>
      {expanded && (
        <div style={{ padding: "0 24px 24px" }}>
          <div style={{
            height: "1px", background: "rgba(201, 162, 39, 0.12)",
            marginBottom: "20px",
          }}/>
          {children}
        </div>
      )}
    </div>
  );
}

// --- Main Home Screen ---
export default function HomeScreen({ username = "Player", onLogout, onCreateLobby, onJoinLobby, onWatchLobby }) {
  const [expandedCard, setExpandedCard] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [gameMode, setGameMode] = useState("replay");
  const [gameDate, setGameDate] = useState(new Date().toISOString().split("T")[0]);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [lobbyCode, setLobbyCode] = useState("");
  const [joinTeamName, setJoinTeamName] = useState("");
  const [watchCode, setWatchCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showRules, setShowRules] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const fetchGames = async () => {
    setLoadingGames(true);
    setError("");
    try {
      const result = await apiCall("getMatches", {
        date: gameDate,
        mock_mode: false,
      });
      if (Array.isArray(result)) {
        setAvailableGames(result);
        setSelectedGames([]);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch games");
    } finally {
      setLoadingGames(false);
    }
  };

  const toggleGame = (eventId) => {
    setSelectedGames((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleCreateLobby = async () => {
    setError("");
    setSuccess("");
    if (!teamName.trim()) {
      setError("Give your team a name.");
      return;
    }
    if (selectedGames.length === 0) {
      setError("Select at least one game.");
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall("createLobby", {
        username,
        match_ids: selectedGames,
        draft_duplicates: 2,
        team_name: teamName.trim(),
        game_mode: gameMode,
      });
      if (result.status === "success") {
        setSuccess(`Lobby created! Code: ${result.lobby_id} — Share this with your crew.`);
        if (onCreateLobby) onCreateLobby(result.lobby_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinLobby = async () => {
    setError("");
    setSuccess("");
    if (!lobbyCode.trim()) {
      setError("Enter a lobby code.");
      return;
    }
    if (!joinTeamName.trim()) {
      setError("Give your team a name.");
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall("joinLobby", {
        username,
        lobbyCode: lobbyCode.trim().toUpperCase(),
        team_name: joinTeamName.trim(),
      });
      if (result.status === "success") {
        setSuccess(`Joined lobby ${result.lobby_id}! Waiting for the draft...`);
        if (onJoinLobby) onJoinLobby(result.lobby_id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // FB-14: watch a lobby read-only; no team name, not counted as a player
  const handleWatchLobby = async () => {
    setError("");
    setSuccess("");
    if (!watchCode.trim()) {
      setError("Enter a lobby code.");
      return;
    }
    setLoading(true);
    try {
      const result = await apiCall("join_as_spectator", {
        username,
        lobbyCode: watchCode.trim().toUpperCase(),
      });
      if (result.status === "success" && onWatchLobby) onWatchLobby(result.lobby_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: FONT_BODY, position: "relative",
    }}>
      <FieldBackground />

      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "440px", padding: "20px",
      }}>
        {/* Header */}
        <div style={{
          background: "rgba(12, 12, 22, 0.85)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "16px",
          padding: "32px 36px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
        }}>
          {/* Top bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: "28px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <OwlIcon />
              <div>
                <h1 style={{
                  fontSize: "18px", fontWeight: 700, color: "#c9a227",
                  margin: 0, letterSpacing: "3px", textTransform: "uppercase",
                  fontFamily: FONT_DISPLAY,
                }}>Black Owl</h1>
                <p style={{
                  fontSize: "12px", color: "#5a5a6e", margin: "2px 0 0",
                  fontFamily: FONT_BODY,
                }}>Welcome, {username}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              style={{
                padding: "8px 14px", fontSize: "11px",
                fontWeight: 600, letterSpacing: "1.5px",
                textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#5a5a6e", background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "6px", cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#c9a227";
                e.target.style.borderColor = "rgba(201,162,39,0.3)";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#5a5a6e";
                e.target.style.borderColor = "rgba(255,255,255,0.08)";
              }}
            >
              Log Out
            </button>
          </div>

          {/* Divider */}
          <div style={{
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(201,162,39,0.2), transparent)",
            marginBottom: "28px",
          }}/>

          {/* Create Lobby Card */}
          <ActionCard
            title="Create a Lobby"
            description="Host a new draft session for your crew"
            expanded={expandedCard === "create"}
            onToggle={() => {
              setExpandedCard(expandedCard === "create" ? null : "create");
              setError("");
              setSuccess("");
            }}
          >
            <Input
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g. The Night Owls"
              icon="🦉"
              maxLength={20}
            />

            {/* Game Mode Toggle */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{
                display: "block", fontSize: "11px", fontWeight: 600,
                letterSpacing: "2px", textTransform: "uppercase",
                color: "#7a7a8e", marginBottom: "8px",
                fontFamily: FONT_BODY,
              }}>Game Mode</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setGameMode("live")}
                  style={{
                    flex: 1, padding: "12px",
                    fontSize: "12px", fontWeight: 600, letterSpacing: "1px",
                    fontFamily: FONT_BODY,
                    color: gameMode === "live" ? "#0a0a14" : "#5a5a6e",
                    background: gameMode === "live" ? "#32c850" : "rgba(255,255,255,0.03)",
                    border: `1.5px solid ${gameMode === "live" ? "#32c850" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: "8px", cursor: "pointer",
                    transition: "all 0.25s ease", textAlign: "center",
                  }}
                >🏈 Live Game</button>
                <button
                  onClick={() => setGameMode("replay")}
                  style={{
                    flex: 1, padding: "12px",
                    fontSize: "12px", fontWeight: 600, letterSpacing: "1px",
                    fontFamily: FONT_BODY,
                    color: gameMode === "replay" ? "#0a0a14" : "#5a5a6e",
                    background: gameMode === "replay" ? "#c9a227" : "rgba(255,255,255,0.03)",
                    border: `1.5px solid ${gameMode === "replay" ? "#c9a227" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: "8px", cursor: "pointer",
                    transition: "all 0.25s ease", textAlign: "center",
                  }}
                >🔄 Test Replay</button>
              </div>
              <p style={{
                fontSize: "11px", color: "#3a3a4e", margin: "8px 0 0",
                fontFamily: FONT_BODY, fontStyle: "italic",
              }}>
                {gameMode === "live"
                  ? "Uses live ESPN data during NFL games"
                  : "Replays a finished game for testing"}
              </p>
            </div>

            {/* Game Date Picker */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{
                display: "block", fontSize: "11px", fontWeight: 600,
                letterSpacing: "2px", textTransform: "uppercase",
                color: "#7a7a8e", marginBottom: "8px",
                fontFamily: FONT_BODY,
              }}>Game Date</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="date"
                  value={gameDate}
                  onChange={(e) => setGameDate(e.target.value)}
                  style={{
                    flex: 1, padding: "12px 14px",
                    fontSize: "13px", fontFamily: FONT_BODY,
                    color: "#e8e6df", background: "rgba(255,255,255,0.03)",
                    border: "1.5px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px", outline: "none",
                    colorScheme: "dark", boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={fetchGames}
                  disabled={loadingGames}
                  style={{
                    padding: "12px 20px",
                    fontSize: "12px", fontWeight: 600, letterSpacing: "1px",
                    fontFamily: FONT_BODY,
                    color: "#c9a227",
                    background: "rgba(201, 162, 39, 0.1)",
                    border: "1.5px solid rgba(201, 162, 39, 0.25)",
                    borderRadius: "8px", cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >{loadingGames ? "..." : "Fetch"}</button>
              </div>
            </div>

            {/* Game List */}
            {availableGames.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  display: "block", fontSize: "11px", fontWeight: 600,
                  letterSpacing: "2px", textTransform: "uppercase",
                  color: "#7a7a8e", marginBottom: "8px",
                  fontFamily: FONT_BODY,
                }}>
                  Select Games
                  {selectedGames.length > 0 && (
                    <span style={{ color: "#c9a227", marginLeft: "8px" }}>
                      ({selectedGames.length} selected)
                    </span>
                  )}
                </label>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {availableGames.map((game) => {
                    const selected = selectedGames.includes(game.event_id);
                    return (
                      <button
                        key={game.event_id}
                        onClick={() => toggleGame(game.event_id)}
                        style={{
                          width: "100%", padding: "12px 14px",
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          background: selected ? "rgba(201, 162, 39, 0.08)" : "rgba(255,255,255,0.02)",
                          border: `1.5px solid ${selected ? "rgba(201, 162, 39, 0.3)" : "rgba(255,255,255,0.06)"}`,
                          borderRadius: "8px", cursor: "pointer",
                          transition: "all 0.25s ease", marginBottom: "6px",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{
                            fontSize: "13px", fontWeight: 600, color: "#e8e6df",
                            margin: "0 0 4px", fontFamily: FONT_BODY,
                          }}>{game.name}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                              display: "inline-block", padding: "2px 6px",
                              fontSize: "9px", fontWeight: 700, letterSpacing: "1px",
                              textTransform: "uppercase",
                              fontFamily: FONT_BODY,
                              color: game.status === "Scheduled" ? "#4a9eff"
                                : game.status === "In Progress" ? "#32c850" : "#78788c",
                              background: game.status === "Scheduled" ? "rgba(74, 158, 255, 0.12)"
                                : game.status === "In Progress" ? "rgba(50, 200, 80, 0.12)" : "rgba(120, 120, 140, 0.12)",
                              border: `1px solid ${game.status === "Scheduled" ? "rgba(74, 158, 255, 0.3)"
                                : game.status === "In Progress" ? "rgba(50, 200, 80, 0.3)" : "rgba(120, 120, 140, 0.3)"}`,
                              borderRadius: "3px",
                            }}>{game.status}</span>
                            <span style={{
                              fontSize: "10px", color: "#5a5a6e",
                              fontFamily: FONT_BODY,
                            }}>{game.start_time}</span>
                          </div>
                        </div>
                        <div style={{
                          width: "22px", height: "22px", borderRadius: "6px",
                          border: `2px solid ${selected ? "#c9a227" : "rgba(255,255,255,0.12)"}`,
                          background: selected ? "#c9a227" : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.25s ease", flexShrink: 0, marginLeft: "10px",
                        }}>
                          {selected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a14" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {availableGames.length === 0 && (
              <p style={{
                fontSize: "12px", color: "#3a3a4e", margin: "0 0 16px",
                fontFamily: FONT_BODY, fontStyle: "italic",
                textAlign: "center",
              }}>
                Pick a date and tap Fetch to see available games
              </p>
            )}

            <button
              onClick={handleCreateLobby}
              disabled={loading || selectedGames.length === 0}
              style={{
                width: "100%", padding: "14px",
                fontSize: "13px", fontWeight: 700,
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: selectedGames.length === 0 ? "#5a5a6e" : "#0a0a14",
                background: selectedGames.length === 0
                  ? "rgba(255,255,255,0.05)"
                  : loading
                    ? "rgba(201, 162, 39, 0.5)"
                    : "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
                border: selectedGames.length === 0 ? "1px solid rgba(255,255,255,0.08)" : "none",
                borderRadius: "8px",
                cursor: selectedGames.length === 0 || loading ? "default" : "pointer",
                transition: "all 0.3s ease",
                boxShadow: selectedGames.length > 0 && !loading ? "0 4px 20px rgba(201, 162, 39, 0.2)" : "none",
              }}
            >
              {loading ? "Creating..." : `Create Lobby (${selectedGames.length} game${selectedGames.length !== 1 ? "s" : ""})`}
            </button>
          </ActionCard>

          {/* Join Lobby Card */}
          <ActionCard
            title="Join a Lobby"
            description="Enter a code to join your friend's draft"
            expanded={expandedCard === "join"}
            onToggle={() => {
              setExpandedCard(expandedCard === "join" ? null : "join");
              setError("");
              setSuccess("");
            }}
          >
            <Input
              label="Lobby Code"
              value={lobbyCode}
              onChange={(e) => setLobbyCode(e.target.value.toUpperCase())}
              placeholder="e.g. K7MPQ4"
              icon="🔑"
              maxLength={6}
            />
            <Input
              label="Team Name"
              value={joinTeamName}
              onChange={(e) => setJoinTeamName(e.target.value)}
              placeholder="e.g. Rival Ravens"
              icon="🦉"
              maxLength={20}
            />
            <button
              onClick={handleJoinLobby}
              disabled={loading}
              style={{
                width: "100%", padding: "14px",
                fontSize: "13px", fontWeight: 700,
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#0a0a14",
                background: loading
                  ? "rgba(201, 162, 39, 0.5)"
                  : "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
                border: "none", borderRadius: "8px",
                cursor: loading ? "wait" : "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 20px rgba(201, 162, 39, 0.2)",
              }}
            >
              {loading ? "..." : "Join Lobby"}
            </button>
          </ActionCard>

          {/* Watch a Game Card (FB-14) */}
          <ActionCard
            title="Watch a Game"
            description="Follow a lobby's draft and game without playing"
            expanded={expandedCard === "watch"}
            onToggle={() => {
              setExpandedCard(expandedCard === "watch" ? null : "watch");
              setError("");
              setSuccess("");
            }}
          >
            <Input
              label="Lobby Code"
              value={watchCode}
              onChange={(e) => setWatchCode(e.target.value.toUpperCase())}
              placeholder="e.g. K7MPQ4"
              icon="👁"
              maxLength={6}
            />
            <button
              onClick={handleWatchLobby}
              disabled={loading}
              style={{
                width: "100%", padding: "14px",
                fontSize: "13px", fontWeight: 700,
                letterSpacing: "2px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#4a9eff",
                background: "transparent",
                border: "1.5px solid rgba(74, 158, 255, 0.5)", borderRadius: "8px",
                cursor: loading ? "wait" : "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {loading ? "..." : "Watch"}
            </button>
          </ActionCard>

          {/* Error / Success messages */}
          {error && (
            <div style={{
              padding: "12px 16px", marginTop: "8px",
              background: "rgba(220, 50, 50, 0.1)",
              border: "1px solid rgba(220, 50, 50, 0.25)",
              borderRadius: "8px", color: "#e85555",
              fontSize: "13px", fontFamily: FONT_BODY,
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              padding: "12px 16px", marginTop: "8px",
              background: "rgba(50, 180, 80, 0.1)",
              border: "1px solid rgba(50, 180, 80, 0.25)",
              borderRadius: "8px", color: "#4ade6a",
              fontSize: "13px", fontFamily: FONT_BODY,
            }}>{success}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            <RulesButton onClick={() => setShowRules(true)} />
            <FeedbackButton onClick={() => setShowFeedback(true)} />
          </div>
          <p style={{
            marginTop: "16px",
            fontSize: "11px", color: "#3a3a4e",
            letterSpacing: "1px", fontFamily: FONT_BODY,
          }}>
            Draft smart. Play bold. Win big.
          </p>
        </div>
      </div>

      {showRules && <RulesPanel onClose={() => setShowRules(false)} />}
      {showFeedback && <FeedbackPanel onClose={() => setShowFeedback(false)} username={username} currentScreen="home" />}

      <style>{`* { box-sizing: border-box; } input::placeholder { color: #3a3a4e; }`}</style>
    </div>
  );
}
